import React, { createContext, useContext, useMemo } from 'react';

import {
  CollectorApi,
  Configuration,
  ConsumerApi,
} from '@rtkwlf/credential-management-service';

import { useAuthContext } from '@packages/legacy/auth';
import { useSelectedOrganization } from '@packages/legacy/organization-context';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';
import { env } from '@packages/utils/env';

const CmsApiContext = createContext<CmsApiContextValue | null>(null);

export const useCmsApiContext = (): CmsApiContextValue => {
  const ctx = useContext(CmsApiContext);
  if (ctx === null) {
    throw new Error('useCmsApiContext must be used within CmsApiProvider');
  } else {
    return ctx;
  }
};

interface CmsApiContextValue {
  collectorApi: CollectorApi;
  consumerApi: ConsumerApi;
}

const ACTIVE_RESPONSE_TEST_TOKEN_KEY = 'ACTIVE_RESPONSE_TEST_TOKEN';

export const CmsApiProvider = ({ children }: React.PropsWithChildren) => {
  const { fetchApi: _fetchApi } = useAuthContext();
  const [testEnvFF] = useFeatureFlag('ACTIVE_RESPONSE_TEST_ENV');
  const { pod } = useSelectedOrganization();

  const isTestEnv = testEnvFF || env.MODE === 'development';

  const token = window.localStorage.getItem(ACTIVE_RESPONSE_TEST_TOKEN_KEY);
  const basePath = isTestEnv
    ? `https://credentialmanagementservice.managedgw.${pod}-test.arcticwolf.net`
    : `https://credentialmanagementservice.managedgw.${pod}-prod.arcticwolf.net`;

  const value = useMemo(() => {
    const fetchApi = isTestEnv ? fetch : _fetchApi;
    const headers = isTestEnv
      ? {
          Authorization: `Bearer ${token}`,
        }
      : undefined;
    const configuration = new Configuration({
      basePath,
      fetchApi,
      headers,
    });

    return {
      collectorApi: new CollectorApi(configuration),
      consumerApi: new ConsumerApi(configuration),
    };
  }, [basePath, token, isTestEnv, _fetchApi]);

  return (
    <CmsApiContext.Provider value={value}>{children}</CmsApiContext.Provider>
  );
};
